import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'

import Input from '../../../components/BaseUI/Input'
import Button from '../../../components/BaseUI/Button'
import Content from '../Content'

import styles from './ResetPassword.module.scss'
import { useTitle } from '../../../hooks/useTitle'
import { Toast, ToastContainer } from '../../../components/CustomUI/Toast'

const API_URL =
  process.env.REACT_APP_API_URL || 'https://dm.aibuddyassistant.com/api'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  useTitle('Сброс пароля | AIBuddy CRM')

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  })
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState(null)
  const [success, setSuccess] = useState(false)
  const [errors, setErrors] = useState({})

  const validateForm = () => {
    const newErrors = {}

    if (!formData.password) {
      newErrors.password = 'Пароль обязателен'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Пароль должен быть минимум 6 символов'
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Пароли не совпадают'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleReset = async () => {
    if (!token) {
      setToast({
        id: 1,
        title: 'Ошибка',
        description: 'Недействительная ссылка для сброса пароля',
        appearance: 'danger',
      })
      return
    }

    if (!validateForm()) {
      return
    }

    setLoading(true)
    setToast(null)

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        password: formData.password,
      })

      setSuccess(true)
      setToast({
        id: 1,
        title: 'Пароль изменен!',
        description: 'Теперь вы можете войти с новым паролем.',
        appearance: 'success',
      })
    } catch (error) {
      console.error('Reset password error:', error)
      const errorMessage = error.response?.data?.error || 'Ошибка сброса пароля'

      setToast({
        id: 1,
        title: 'Ошибка',
        description: errorMessage,
        appearance: 'danger',
      })
    } finally {
      setLoading(false)
    }
  }

  const removeToast = () => {
    setToast(null)
  }

  if (!token) {
    return (
      <div className={styles['reset-container']}>
        <Content title="Недействительная ссылка">
          <div className={styles.content}>
            <p className={styles.message}>
              Ссылка для сброса пароля недействительна или устарела.
            </p>
            <div className={styles.actions}>
              <Button
                title="Запросить новую ссылку"
                appearance="primary"
                isBlock={true}
                onClick={() => navigate('/recover-password')}
              />
            </div>
          </div>
        </Content>
      </div>
    )
  }

  if (success) {
    return (
      <div className={styles['reset-container']}>
        <Content title="Пароль изменен">
          <div className={styles.content}>
            <div className={styles.iconSuccess}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <p className={styles.message}>
              Ваш пароль успешно изменен. Теперь вы можете войти в систему с
              новым паролем.
            </p>
            <div className={styles.actions}>
              <Button
                title="Войти в систему"
                appearance="primary"
                isBlock={true}
                onClick={() => navigate('/login', { replace: true })}
              />
            </div>
          </div>
        </Content>

        {toast && (
          <ToastContainer onDismissed={removeToast}>
            <Toast {...toast} isExpanded />
          </ToastContainer>
        )}
      </div>
    )
  }

  return (
    <div className={styles['reset-container']}>
      <Content title="Новый пароль">
        <div className="mt-4">
          <Input
            type="password"
            label="Новый пароль"
            placeholder="Минимум 6 символов"
            className={styles['custom__input']}
            value={formData.password}
            onChange={value => {
              setFormData({ ...formData, password: value })
              if (errors.password) setErrors({ ...errors, password: '' })
            }}
            error={errors.password}
          />

          <Input
            type="password"
            label="Подтверждение пароля"
            placeholder="Повторите новый пароль"
            className={styles['custom__input']}
            value={formData.confirmPassword}
            onChange={value => {
              setFormData({ ...formData, confirmPassword: value })
              if (errors.confirmPassword)
                setErrors({ ...errors, confirmPassword: '' })
            }}
            error={errors.confirmPassword}
          />
        </div>

        <div className="mt-2">
          <Button
            title={loading ? 'Сохранение...' : 'Сохранить пароль'}
            appearance="primary"
            isBlock={true}
            onClick={handleReset}
            disabled={loading}
          />
        </div>

        <div className={styles['register']}>
          <span className={styles['link']} onClick={() => navigate('/login')}>
            Вернуться на страницу входа
          </span>
        </div>
      </Content>

      {toast && (
        <ToastContainer onDismissed={removeToast}>
          <Toast {...toast} isExpanded />
        </ToastContainer>
      )}
    </div>
  )
}
